"use client";

import React, { useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { SponsorStep1 } from "./SponsorStep1";
import { SponsorStep2 } from "./SponsorStep2";
import { SponsorStep3 } from "./SponsorStep3";
import { Button } from "~/components/v3/themed/Button";
import { api } from "~/trpc/react";
import Stepper from "./Step";
import {
    applyToSponsorEventWithPackage,
    createSponsorCheckoutSession,
    createTestSponsorPackage,
    getSponsorPackageForEvent,
    GetSponsorPackageForEvent,
} from "../actions";
import { useSession } from "next-auth/react";
import { toastHandler } from "~/components/ToastHandler";
import { useRouter } from "next/navigation";
import { SponsorStep4 } from "./SponsorStep4";
import Breadcrumbs from "~/components/v3/themed/BreadCrumbs";
import { ApplyToSponsorFormData } from "./types";
import RiverLoading from "~/components/themed/RiverLoading";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "~/components/v3/shadcn/Card";
import Link from "next/link";
import { sanitizeEmail } from "~/utils/formatters";
import LoginModal from "~/components/v3/themed/LoginModal";
import FloatingSponsorIndicator from "~/app/v1/[communitySlug]/sponsors/components/FloatingSponsorIndicator";
import { cn } from "~/utils/fontsConfig";

type Props = {
    eventSlug: string;
    defaultValues: ApplyToSponsorFormData;
};

const ApplyToSponsorEventFlow = ({ eventSlug, defaultValues }: Props) => {
    const router = useRouter();
    const { data: session } = useSession();

    const { data: event, isLoading: isEventLoading } =
        api.event.getBySlug.useQuery(eventSlug, {
            refetchOnWindowFocus: false,
            refetchOnMount: false,
        });

    const [showLoginModal, setShowLoginModal] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [sponsorApplicationId, setSponsorApplicationId] = useState<string>();
    const [sponsorPackage, setSponsorPackage] =
        useState<GetSponsorPackageForEvent>();
    const [isPackageLoading, setIsPackageLoading] = useState(true);

    const [selectedEventIds, setSelectedEventIds] = useState<Set<string>>(
        new Set(),
    );
    useEffect(() => {
        if (event) {
            setSelectedEventIds(new Set([event.id]));
        }
    }, [event]);

    const form = useForm<ApplyToSponsorFormData>({
        defaultValues: defaultValues,
    });

    useEffect(() => {
        const fetchSponsorPackage = async () => {
            if (!event || !event.communityId) return;
            setIsPackageLoading(true);
            try {
                const data = await getSponsorPackageForEvent(
                    event?.communityId,
                    event.id,
                );
                setSponsorPackage(data);
            } catch (error) {
                console.error("Error fetching sponsor package:", error);
            } finally {
                setIsPackageLoading(false);
            }
        };

        if (event) fetchSponsorPackage();
    }, [event]);

    const handleCreateTestPackage = async () => {
        await toastHandler({
            loadingMessage: "Creating test sponsor package...",
            successMessage: "Test sponsor package created successfully",
            errorMessage: "Failed to create test sponsor package",
            operation: () => createTestSponsorPackage(),
            onSuccess: () => {
                // Refetch the event and sponsor package
                router.refresh();
            },
            onError: (error) => {
                console.error("Error creating test sponsor package:", error);
            },
        });
    };

    const handleApplyToSponsor = async () => {
        if (!event?.id) return;
        toastHandler({
            loadingMessage: "Processing sponsor application...",
            successMessage: "Application submitted!",
            errorMessage: "Failed to submit application",
            operation: async () => {
                const input = {
                    eventId: event.id,
                    communityId: event.communityId,
                    name: form.getValues("name"),
                    email: sanitizeEmail(form.getValues("email")),
                    description: form.getValues("description"),
                    additionalInfo: form.getValues("additionalInfo"),
                    image: form.getValues("image"),
                    selectedTierId: form.getValues("selectedPackageTier").id,

                    customPriceDollars: form.getValues().customPriceDollars
                        ? Number(form.getValues().customPriceDollars)
                        : undefined,
                    customPriceCents: form.getValues().customPriceCents
                        ? Number(form.getValues().customPriceCents)
                        : undefined,
                    customDuration: form.getValues().customDuration
                        ? Number(form.getValues().customDuration)
                        : undefined,
                };

                const result = await applyToSponsorEventWithPackage(input);

                setSponsorApplicationId(result?.sponsorApplication?.id);

                return result;
            },
            onSuccess: (result) => {
                router.push(`/events/${event.slug}`);
                window.open(
                    `/community-management/${event?.community?.slug}/sponsors/applications/${result?.sponsorApplication?.id}`,
                    "_blank",
                );
            },
            onError: (error) => {
                console.error("Error applying to sponsor:", error);
            },
        });
    };

    // const handleCompletePayment = async () => {
    //     if (!event?.id || !session?.user?.id || !event.slug) return;
    //     const selectedTier = form.getValues("selectedPackageTier");
    //     if (!selectedTier || !selectedTier.id) {
    //         console.error("No selected tier found");
    //         return;
    //     }
    //
    //     toastHandler({
    //         loadingMessage: "Creating checkout session...",
    //         successMessage: "Redirecting to payment...",
    //         errorMessage: "Failed to create checkout session",
    //         operation: async () => {
    //             if (!sponsorApplicationId) {
    //                 toast.error("Sponsor application ID not found");
    //                 return;
    //             }
    //
    //             const checkoutUrl = await createSponsorCheckoutSession({
    //                 userId: session.user.id,
    //                 eventId: event.id,
    //                 eventSlug: event.slug,
    //                 communityId: event.communityId,
    //                 sponsorTierId: selectedTier.id,
    //                 sponsorApplicationId: sponsorApplicationId,
    //             });
    //             if (checkoutUrl) {
    //                 window.location.href = checkoutUrl;
    //             } else {
    //                 throw new Error("Failed to create checkout session");
    //             }
    //         },
    //         onError: (error) => {
    //             console.error("Error creating checkout session:", error);
    //         },
    //     });
    // };

    const getNextButtonLabel = () => {
        if (currentStep === 2) return "Submit";
        return "Continue";
    };

    const handleNextOrSubmit = () => {
        if (currentStep < 2) {
            setCurrentStep(currentStep + 1);
        } else if (currentStep === 2) {
            handleApplyToSponsor();
        }
        // else if (currentStep === 3) {
        //     handleCompletePayment();
        // }
    };

    const handlePrev = () => {
        if (currentStep > 0) setCurrentStep(currentStep - 1);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
    };

    const handleCancel = () => {
        toastHandler({
            loadingMessage: "Cancelling...",
            successMessage: "Sponsor application cancelled",
            errorMessage: "Failed to cancel sponsor application",
            operation: async () => {
                if (!event?.slug) {
                    router.push(`/home`);
                } else {
                    router.push(`/events/${event.slug}`);
                }
            },
        });
    };

    if (isEventLoading || isPackageLoading) {
        return <RiverLoading />;
    }

    if (!session) {
        return (
            <Card className="mx-auto mt-8 max-w-2xl">
                <CardHeader>
                    <CardTitle>Log in to Apply to Sponsor</CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="mb-4 text-v3-N-300">
                        You must be logged in to apply to a sponsor for this
                        event.
                    </p>
                    <Button
                        variant="default"
                        onClick={() => setShowLoginModal(true)}
                    >
                        Continue
                    </Button>

                    <LoginModal
                        title="Sign Up or Log in to Apply to Sponsor"
                        isOpen={showLoginModal}
                        setIsOpen={setShowLoginModal}
                        description="Sign Up to Apply to Sponsor Events on River!"
                    />
                </CardContent>
            </Card>
        );
    }

    if (!event) {
        return (
            <Card className="mx-auto mt-8 max-w-2xl">
                <CardHeader>
                    <CardTitle>Event Not Found</CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="mb-4 text-v3-N-300">
                        We're sorry, but we couldn't find the event you're
                        looking for.
                    </p>
                    <Link href="/home" passHref>
                        <Button variant="default">Back to Home</Button>
                    </Link>
                </CardContent>
            </Card>
        );
    }

    if (!sponsorPackage || sponsorPackage.packageTiers.length === 0) {
        return (
            <Card className="mx-auto mt-8 max-w-2xl">
                <CardHeader>
                    <CardTitle>Sponsorship Unavailable</CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="mb-4 text-v3-N-300">
                        We're sorry, but sponsorship packages are not available
                        for this event at the moment.
                    </p>
                    <p className="mb-4 text-v3-N-300">
                        Please check back later or contact the event organizers
                        for more information.
                    </p>
                    <Link href={`/events/${eventSlug}`} passHref>
                        <Button variant="default">Back to Event Details</Button>
                    </Link>
                    {session?.user?.role === "ADMIN" && (
                        <div className="mt-4 border-t border-v3-N-400 pt-4">
                            <p className="mb-2 text-v3-N-300">Admin Actions:</p>
                            <Button
                                variant="info"
                                onClick={handleCreateTestPackage}
                            >
                                Create Test Sponsor Package
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>
        );
    }

    const steps = [
        <SponsorStep1
            key="step1"
            event={event}
            sponsorshipPackage={sponsorPackage}
        />,
        <SponsorStep2 key="step2" />,
        <SponsorStep3 key="step3" events={[event]} />,
        // <SponsorStep4 key="step4" />,
    ];

    return (
        <FormProvider {...form}>
            <form
                onSubmit={handleSubmit}
                className={cn("flex w-[100%] flex-1", "mt-[20%] md:mt-0")}
            >
                <div className="flex w-full flex-col bg-black text-white">
                    <Breadcrumbs />
                    <div>
                        <Stepper
                            currentStep={currentStep}
                            stepRanges={{
                                start: 0,
                                end: 2,
                            }}
                        />
                    </div>

                    {steps[currentStep]}

                    <FloatingSponsorIndicator
                        currentStep={currentStep}
                        selectedTier={form.watch("selectedPackageTier")}
                        selectedEventIds={selectedEventIds}
                        onNext={handleNextOrSubmit}
                        onBack={currentStep > 0 ? handlePrev : undefined}
                        onCancel={handleCancel}
                        nextLabel={getNextButtonLabel()}
                        isEventMode={true}
                    />
                </div>
            </form>
        </FormProvider>
    );
};

export default ApplyToSponsorEventFlow;
